import gql from 'graphql-tag';
import React from 'react';

import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import ScrollableTable from 'ui-library/scrollable/ScrollableTable';
import {
  RowSelectionMode,
  TABLE_KEYS,
  TABLE_ROW_BODY_HEIGHT,
} from 'ui-library/scrollable/ScrollableTableConstants';

import {
  CreateTestOrdersModalQuery,
  CreateTestOrdersModalQueryVariables,
} from './__generated/CreateTestOrdersModalQuery';
import {TestOrderCreationRowData} from './CreateTestOrdersModal';
import useTestOrderCreationTable from './useTestOrderCreationTable';

type MaybeInaccessibleProcedure = CreateTestOrdersModalQuery['procedure'];
export type ProcedureWithTestDefinitions = ExtractByTypename<MaybeInaccessibleProcedure, 'ProcedureNode'>;
export type AllowedDilutionFactor =
  ProcedureWithTestDefinitions['currentProcedureFlowchartVersion']['nodes'][0]['dilutionPlan']['allowedDilutionFactors'][0];

export const CREATE_TEST_ORDERS_MODAL_QUERY = gql`
  query CreateTestOrdersModalQuery($procedureId: StrictString!) @skipAudit @engComponent(name: PROCEDURES) {
    procedure(id: $procedureId) {
      ... on ProcedureNode {
        id
        currentProcedureFlowchartVersion {
          id
          nodes {
            testDefinitions {
              ... on TestDefinitionNode {
                id
                name
              }
              ... on InaccessibleInfoNode {
                ...InaccessibleInfoGraphQL
              }
            }
            dilutionPlan {
              allowedDilutionFactors
            }
          }
        }
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface BaseTestOrderCreationTableProps {
  selectedSampleId: string;
  fullTestOrderData: Array<TestOrderCreationRowData>;
  setFullTestOrderData: React.Dispatch<React.SetStateAction<Array<TestOrderCreationRowData>>>;
  tableHeader: JSX.Element;
}

interface TestOrderCreationTableProps extends BaseTestOrderCreationTableProps {
  procedure: ProcedureWithTestDefinitions;
}

interface QueryingTestOrderCreationTableProps extends BaseTestOrderCreationTableProps {
  procedureId: string;
}

export function TestOrderCreationTable({
  procedure,
  selectedSampleId,
  fullTestOrderData,
  setFullTestOrderData,
  tableHeader,
}: TestOrderCreationTableProps): JSX.Element {
  const {columnHeaders, rows, handleRowsSelect} = useTestOrderCreationTable({
    procedure,
    selectedSampleId,
    fullTestOrderData,
    setFullTestOrderData,
  });

  const getRowContentHeight = (rowIdx: number): number => TABLE_ROW_BODY_HEIGHT;

  if (rows.length === 0) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text="No test definitions are available for this assay template."
      />
    );
  }
  return (
    <div {...getTestProps({component: 'CreateTestOrdersModal', element: 'testOrderDataTable'})}>
      {tableHeader}
      <ScrollableTable
        rowSelectionMode={RowSelectionMode.MULTIPLE}
        disableSelection={false}
        columns={columnHeaders}
        rows={rows}
        tableKey={TABLE_KEYS.CREATE_TEST_ORDERS_TABLE}
        canResizeColumns={true}
        onRowsSelect={handleRowsSelect}
        getRowContentHeight={getRowContentHeight}
      />
    </div>
  );
}

/**
 * QueryingTestOrderCreationTable queries for the given procedure and renders the TestOrderCreationTable
 * based on that data. Essentially a light querying wrapper.
 */
export function QueryingTestOrderCreationTable({
  procedureId,
  selectedSampleId,
  fullTestOrderData,
  setFullTestOrderData,
  tableHeader,
}: QueryingTestOrderCreationTableProps): JSX.Element {
  return (
    <BenchlingQuery<CreateTestOrdersModalQuery, CreateTestOrdersModalQueryVariables>
      query={CREATE_TEST_ORDERS_MODAL_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{
        procedureId,
      }}
    >
      {(data) =>
        isInaccessible(data.procedure) || isInaccessible(data.procedure.currentProcedureFlowchartVersion) ? (
          <CenteredHorizontalThrobber />
        ) : (
          <TestOrderCreationTable
            procedure={data.procedure}
            selectedSampleId={selectedSampleId}
            fullTestOrderData={fullTestOrderData}
            setFullTestOrderData={setFullTestOrderData}
            tableHeader={tableHeader}
          />
        )
      }
    </BenchlingQuery>
  );
}
